import { ChecklistCriteria, CriteriaInstance } from "types/Checklist";
import { CellTextParagraphs } from "types/WordChecklist";
import { useShouldIncludeChecklistCriteriaCheckmark } from "../useShouldIncludeChecklistCriteriaCheckmark";

const CHECKMARK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path d="M2 6.5 L4.5 9 L10 3.5" stroke="#000000" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const useChecklistMarkCell = (includeCriteriaCheckmarks: boolean) => {
  if (!includeCriteriaCheckmarks) {
    return {
      text: "",
      alignment: "center",
    };
  }

  return {
    svg: CHECKMARK_SVG,
    fit: [10, 10],
    alignment: "center",
    margin: [0, 2, 0, 0],
  };
};

export const usePDFChecklistCriteriaGenerator = (
  criteriaInstance: CriteriaInstance,
  allCriteria: ChecklistCriteria[],
  numParagraphs: number,
  numParagraphColumns: number,
  cellTextToParagraphs: CellTextParagraphs[],
  color: string = "000000",
  hideScores: boolean = false,
  includeCriteriaCheckmarks: boolean = false,
  isStudentMode: boolean = false
) => {
  const allRows = [];
  const criteria = criteriaInstance.checklistCriteriaId
    ? useGetCriteriaFromId(allCriteria, criteriaInstance.checklistCriteriaId)
    : undefined;

  if (criteria || criteriaInstance.isCustom) {
    const criteriaLabel =
      criteria?.label ?? criteriaInstance.labelOverride ?? "N/A";

    let label = [
      {
        text: criteriaLabel,
        decoration: criteriaInstance.ignore ? ["lineThrough"] : undefined,
      },
    ] as any[];

    for (const cellTextParagraphsInstance of cellTextToParagraphs) {
      const cellText = cellTextParagraphsInstance.text
        .replace(/\n/g, "") // Remove newline characters
        .toLowerCase() // Convert to lowercase
        .replace(/\s+/g, " ") // Replace multiple spaces with a single space
        .trim(); // Remove leading and trailing spaces

      const newCriteriaLabel = criteriaLabel
        .replace(/\n/g, "") // Remove newline characters
        .toLowerCase() // Convert to lowercase
        .replace(/\s+/g, " ") // Replace multiple spaces with a single space
        .trim(); // Remove leading and trailing spaces

      if (
        cellText.includes(newCriteriaLabel) ||
        newCriteriaLabel.includes(cellText)
      ) {
        label = [] as any[];
        for (const paragraph of cellTextParagraphsInstance.paragraphs) {
          for (const token of paragraph.tokens) {
            const textRun = useConvertTokenToPDFText(
              token,
              paragraph.formatting.alignment,
              paragraph.formatting.lineSpacing
            );

            textRun.decoration = criteriaInstance.ignore
              ? ["lineThrough"]
              : undefined;

            label.push(textRun);
          }

          label.push({
            text: "",
          });
        }
      }
    }

    const criteriaRow: any[] = [
      useChecklistMarkCell(
        useShouldIncludeChecklistCriteriaCheckmark(
          criteriaInstance,
          includeCriteriaCheckmarks,
          isStudentMode
        )
      ),
      label,
    ];

    if (
      criteriaInstance.evaluationMethod == "paragraph" &&
      criteriaInstance.paragraphPositionsData &&
      criteriaInstance.paragraphPositionsData.length > 1
    ) {
      const numEarnedPointsParagraphs =
        criteriaInstance.paragraphPositionsData.length;

      let columnSpan = Math.floor(
        numParagraphColumns / numEarnedPointsParagraphs
      );
      let totalColumns = 0;
      let index = 0;

      const scoreCells = [] as any[];

      for (const paragraphPositionData of criteriaInstance.paragraphPositionsData) {
        const paragraphPoints =
          paragraphPositionData.manualScore ?? paragraphPositionData.score ?? 0;
        totalColumns += columnSpan;

        // If the current key is the last keym and the total columns isn't yet equal to numParagraphs
        // Then we need to set columnSpan to the difference between numParagraphs and totalColumns
        if (
          index == numEarnedPointsParagraphs - 1 &&
          totalColumns != numParagraphColumns
        ) {
          columnSpan += numParagraphColumns - totalColumns;
        }

        var scoreCellText = "0";

        if (paragraphPoints) {
          scoreCellText = paragraphPoints.toString();
        }

        if (criteriaInstance.pointsPerInstance) {
          scoreCellText += `/${criteriaInstance.pointsPerInstance}`;
        }

        if(criteriaInstance.pointsPerInstance == 0 || hideScores) {
          scoreCellText = "";
        }

        scoreCells.push({
          text: criteriaInstance.ignore != true ? scoreCellText : "",
          alignment: "center",
        });

        index++;
      }

      const scoreCellIndex = 0;

      const scoreTable = {
        table: {
          widths: scoreCells.map(() => "*"),
          body: [scoreCells],
        },
        layout: {
          hLineWidth: function (i: any, node: any) {
            return 0; // No horizontal lines
          },
          vLineWidth: function (i: any, node: any) {
            // Add right border to the first two cells
            if (
              i > 0 &&
              i < (criteriaInstance.paragraphPositionsData?.length ?? 1)
            ) {
              return 0.5; // Adjust the width as needed
            } else {
              return 0;
            }
          },
          vLineColor: function (i: any, node: any) {
            if (
              i > 0 &&
              i < (criteriaInstance.paragraphPositionsData?.length ?? 1)
            ) {
              return "black"; // Adjust the color as needed
            }
          },
          paddingLeft: function (i: any, node: any) {
            return 8;
          },
          paddingRight: function (i: any, node: any) {
            return 8;
          },
          paddingTop: function (i: any, node: any) {
            return 0;
          },
          paddingBottom: function (i: any, node: any) {
            return 0;
          },
        },
      };

      criteriaRow.push(scoreTable);
    } else {
      var scoreCellText = "";

      if (!criteriaInstance.earnedPoints) {
        criteriaInstance.earnedPoints = 0;
      }

      if (criteria && criteria.showPointsPlus) {
        scoreCellText = `+${criteriaInstance.earnedPoints.toString()}`;
      } else if (
        criteriaInstance.earnedPoints != 0 ||
        criteriaInstance.maxPoints != undefined
      ) {
        scoreCellText = criteriaInstance.earnedPoints.toString();
      }

      if (criteriaInstance.maxPoints) {
        scoreCellText += `/${criteriaInstance.maxPoints}`;
      }

      if(criteriaInstance.maxPoints == 0 || hideScores) {
        scoreCellText = "";
      }

      criteriaRow.push({
        text: criteriaInstance.ignore != true ? scoreCellText : "",
        alignment: "center",
      });
    }

    allRows.push(criteriaRow);
  }

  return allRows;
};
